import { ReactElement, useMemo } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { useLocation } from 'react-router-dom'
import DashboardHeader from 'app/components/analytics/DashboardHeader'
import DashboardTabs from 'app/components/analytics/DashboardTabs'
import SummaryTab from 'app/components/analytics/SummaryTab'
import { PageScroll } from 'app/components/layout/PageScroll'
import SimpleTable from 'app/components/table/SimpleTable'
import { UsageCostChart, type UsageGranularity } from 'app/components/usage-tracking/UsageCostChart'
import {
  formatLatencyMs,
  formatScaledUnitCost,
  formatUsd,
  formatUsdOrDash,
} from 'app/components/usage-tracking/usageCostFormat'
import type { UsageProductTab, UsageView, UseUsageTrackingReturn } from 'app/components/usage-tracking/useUsageTracking'
import SegmentControl from 'app/components/viz/SegmentControl'
import { getUsageLocationColumns } from 'app/table-columns/analytics/usage-tracking'
import type { StatsCardType } from 'types/StatsCardType'
import type { UsageCostQueryData } from 'app/components/usage-tracking/graphql'

export type UsageTrackingComponentProps = UseUsageTrackingReturn

const PRODUCT_OPTIONS: { label: string; value: UsageProductTab }[] = [
  { label: `All`, value: `All` },
  { label: `Voice Agent`, value: `Voice Agent` },
  { label: `SMS Agent`, value: `SMS Agent` },
]
const GRANULARITY_OPTIONS: UsageGranularity[] = [`hourly`, `daily`, `weekly`, `monthly`]

function granularityLabel(granularity: UsageGranularity): string {
  return granularity.charAt(0).toUpperCase() + granularity.slice(1)
}

function chartTitleForTab(tab: UsageProductTab): string {
  if (tab === `Voice Agent`) return `Voice cost`
  if (tab === `SMS Agent`) return `SMS cost`
  return `Voice vs SMS cost`
}

type CostCompositionItem = { label: string; value: number; color: string }

const COMPOSITION_COLORS = [`#3B82F6`, `#10B981`, `#F59E0B`]

function CostCompositionPanel({ title, items }: { title: string; items: CostCompositionItem[] }): ReactElement {
  const total = items.reduce((sum, item) => sum + item.value, 0)
  const sorted = [...items].sort((a, b) => b.value - a.value)
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-default p-5">
      <p className="text-ui-sm text-strong">{title}</p>

      {total > 0 && (
        <div className="flex h-3 w-full overflow-hidden rounded-full">
          {sorted.map((item) => (
            <div
              key={item.label}
              className="h-full"
              style={{ width: `${(item.value / total) * 100}%`, backgroundColor: item.color }}
              title={`${item.label}: ${formatUsd(item.value)} (${Math.round((item.value / total) * 100)}%)`}
            />
          ))}
        </div>
      )}

      <ul className="flex flex-col gap-3">
        {sorted.map((item) => (
          <li key={item.label} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-body-sm text-strong">{item.label}</span>
            </div>
            <span className="shrink-0 text-ui-sm tabular-nums text-strong">
              {formatUsd(item.value)}
              {total > 0 && (
                <span className="ml-1 text-body-xs text-subtle">({Math.round((item.value / total) * 100)}%)</span>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between border-t border-default pt-3 mt-auto">
        <span className="text-ui-sm text-default">Total</span>
        <span className="text-ui-sm tabular-nums text-strong">{formatUsd(total)}</span>
      </div>
    </div>
  )
}

function buildVoiceComposition(summary: UsageCostQueryData[`summary`]): CostCompositionItem[] {
  return [
    {
      label: `Telephony (${summary.voiceCalls.toLocaleString(`en-US`)} calls)`,
      value: summary.telephonyCostUsd,
      color: COMPOSITION_COLORS[0],
    },
    { label: `ElevenLabs LLM`, value: summary.elevenLabsLlmUsd, color: COMPOSITION_COLORS[1] },
    { label: `ElevenLabs platform`, value: summary.elevenLabsPlatformUsd, color: COMPOSITION_COLORS[2] },
  ]
}

function buildSmsComposition(summary: UsageCostQueryData[`summary`]): CostCompositionItem[] {
  return [
    { label: `SMS LLM`, value: summary.smsLlmCostUsd, color: COMPOSITION_COLORS[0] },
    {
      label: `Carrier fees (${summary.smsConversations.toLocaleString(`en-US`)} conversations)`,
      value: summary.smsCarrierCostUsd,
      color: COMPOSITION_COLORS[1],
    },
  ]
}

// Product owner directive: cost-per-X cards lead every view; totals are
// secondary.
function buildStats(
  tab: UsageProductTab,
  summary: UsageCostQueryData[`summary`],
  { voiceTransferredCalls, avgSmsLatencyMs }: { voiceTransferredCalls: number; avgSmsLatencyMs: number | undefined },
): StatsCardType[] {
  const costPerCall = formatScaledUnitCost(summary.avgCostPerVoiceCall, `Call`, `Calls`)
  const costPerConversation = formatScaledUnitCost(summary.avgCostPerSmsConversation, `Conversation`, `Conversations`)

  if (tab === `Voice Agent`) {
    return [
      {
        ...costPerCall,
        infoTooltip: {
          title: `Cost / Call`,
          message: `Average cost incurred each time the voice agent handles a call. Includes telephony, STT, TTS, and LLM inference. The label auto-scales (per 100 or 1,000 calls) so the dollar value stays readable.`,
        },
      },
      {
        label: `Voice Cost`,
        value: formatUsdOrDash(summary.totalCostUsd),
        infoTooltip: {
          title: `Voice Cost`,
          message: `Total dollar amount billed for all voice agent activity — telephony minutes, speech-to-text transcription, text-to-speech synthesis, and LLM token usage — within the selected date range.`,
        },
      },
      {
        label: `Voice Calls`,
        value: summary.voiceCalls.toLocaleString(`en-US`),
        infoTooltip: {
          title: `Voice Calls`,
          message: `Count of individual inbound and outbound calls processed by the voice agent. Each unique phone connection counts as one call regardless of duration or transfers.`,
        },
      },
      {
        label: `Voice Minutes`,
        value: summary.voiceMinutes.toLocaleString(`en-US`),
        infoTooltip: {
          title: `Voice Minutes`,
          message: `Cumulative talk time across all voice agent calls, measured from answer to hang-up. Hold time and ring time are excluded.`,
        },
      },
      {
        label: `Transferred Calls`,
        value: voiceTransferredCalls.toLocaleString(`en-US`),
        infoTooltip: {
          title: `Transferred Calls`,
          message: `Calls the voice agent handed off to the office. Transfers still incur agent cost for the AI leg, so a high transfer rate raises cost without a booked outcome.`,
        },
      },
    ]
  }

  if (tab === `SMS Agent`) {
    return [
      {
        ...costPerConversation,
        infoTooltip: {
          title: `Cost / Conversation`,
          message: `Average cost for a single SMS conversation thread. Includes carrier message fees and LLM token costs. The label auto-scales (per 100 or 1,000 conversations) so the dollar value stays readable.`,
        },
      },
      {
        label: `SMS Cost`,
        value: formatUsdOrDash(summary.totalCostUsd),
        infoTooltip: {
          title: `SMS Cost`,
          message: `Total dollar amount billed for all SMS agent activity — carrier segment fees plus LLM inference for generating replies — within the selected date range.`,
        },
      },
      {
        label: `SMS Conversations`,
        value: summary.smsConversations.toLocaleString(`en-US`),
        infoTooltip: {
          title: `SMS Conversations`,
          message: `Count of distinct SMS conversation threads handled by the agent. A conversation groups all messages exchanged with one contact until the thread is considered closed.`,
        },
      },
      {
        label: `Input Tokens`,
        value: summary.totalTokensInput.toLocaleString(`en-US`),
        infoTooltip: {
          title: `Input Tokens`,
          message: `Total tokens sent to the LLM as prompt context (system instructions, conversation history, and the patient's latest message) when generating SMS replies.`,
        },
      },
      {
        label: `Output Tokens`,
        value: summary.totalTokensOutput.toLocaleString(`en-US`),
        infoTooltip: {
          title: `Output Tokens`,
          message: `Total tokens generated by the LLM in its replies. Output tokens are typically more expensive than input tokens and directly correlate with response length.`,
        },
      },
      ...(avgSmsLatencyMs != null
        ? [
            {
              label: `Avg Response Latency`,
              value: formatLatencyMs(avgSmsLatencyMs),
              infoTooltip: {
                title: `Avg Response Latency`,
                message: `Average time the SMS agent takes to generate a reply, measured from receiving the patient's message to the LLM response completing. Excludes the template-based initial message.`,
              },
            },
          ]
        : []),
    ]
  }

  return [
    {
      ...costPerCall,
      infoTooltip: {
        title: `Cost / Call`,
        message: `Average cost incurred each time the voice agent handles a call. Includes telephony, STT, TTS, and LLM inference. The label auto-scales (per 100 or 1,000 calls) so the dollar value stays readable.`,
      },
    },
    {
      ...costPerConversation,
      infoTooltip: {
        title: `Cost / Conversation`,
        message: `Average cost for a single SMS conversation thread. Includes carrier message fees and LLM token costs. The label auto-scales (per 100 or 1,000 conversations) so the dollar value stays readable.`,
      },
    },
    {
      label: `Total Cost`,
      value: formatUsdOrDash(summary.totalCostUsd),
      infoTooltip: {
        title: `Total Cost`,
        message: `Combined dollar spend across voice and SMS agents — telephony, carrier fees, speech services, and LLM inference — for the selected date range.`,
      },
    },
    {
      label: `Voice Calls`,
      value: summary.voiceCalls.toLocaleString(`en-US`),
      infoTooltip: {
        title: `Voice Calls`,
        message: `Count of individual inbound and outbound calls processed by the voice agent. Each unique phone connection counts as one call regardless of duration or transfers.`,
      },
    },
    {
      label: `SMS Conversations`,
      value: summary.smsConversations.toLocaleString(`en-US`),
      infoTooltip: {
        title: `SMS Conversations`,
        message: `Count of distinct SMS conversation threads handled by the agent. A conversation groups all messages exchanged with one contact until the thread is considered closed.`,
      },
    },
    {
      label: `Voice Minutes`,
      value: summary.voiceMinutes.toLocaleString(`en-US`),
      infoTooltip: {
        title: `Voice Minutes`,
        message: `Cumulative talk time across all voice agent calls, measured from answer to hang-up. Hold time and ring time are excluded.`,
      },
    },
    {
      label: `Tokens`,
      value: (summary.totalTokensInput + summary.totalTokensOutput).toLocaleString(`en-US`),
      infoTooltip: {
        title: `Tokens`,
        message: `Combined LLM input and output tokens consumed across voice and SMS agents. Reflects overall AI compute usage and is the primary driver of inference cost.`,
      },
    },
  ]
}

function viewToTab(view: UsageView): string {
  return view === `summary` ? `Summary` : `Location Breakdown`
}

function tabToView(tab: string): UsageView {
  return tab === `Summary` ? `summary` : `locations`
}

export function buildUsageLocationBaseUrl(pathname: string, search: string): string {
  const searchParams = new URLSearchParams(search)
  searchParams.delete(`locationDetail`)
  const serializedSearch = searchParams.toString()
  return `${pathname}${serializedSearch ? `?${serializedSearch}` : ``}`
}

export function UsageTrackingComponent(props: UsageTrackingComponentProps): ReactElement {
  const {
    openTab,
    view,
    granularity,
    fromDate,
    toDate,
    summary,
    timeSeries,
    filteredLocationRows,
    isLoading,
    errorMessage,
    voiceTransferredCalls,
    avgSmsLatencyMs,
    handleTabChange,
    handleViewChange,
    handleGranularityChange,
    handleDateChange,
    setLocationSearchQuery,
  } = props

  const location = useLocation()
  // Strip any existing drill-down param so the cell links stay idempotent across renders.
  const baseUrl = buildUsageLocationBaseUrl(location.pathname, location.search)
  const locationColumns = useMemo(
    () => getUsageLocationColumns({ fromDate, toDate, baseUrl }),
    [fromDate, toDate, baseUrl],
  )

  const productSelect = (
    <Select value={openTab} onValueChange={(val) => handleTabChange(val as UsageProductTab)}>
      <SelectTrigger
        id="usage-product"
        className="h-8 w-auto min-w-[8rem] rounded-[6px] border-(--fg-strong)/10 bg-surface text-[12px] font-medium leading-[15px] text-default shadow-none hover:border-(--fg-strong)/16 hover:bg-(--fg-strong)/2 hover:text-strong"
        aria-label="Product"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PRODUCT_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const header = (
    <>
      <DashboardHeader
        title="Usage Tracking"
        dateChangeHandler={handleDateChange}
        hideCompare
        hideGroupedLocationOptions
        defaultDateRange="last_30_days"
        hideChannelSelector
        hideUniqueNewPatientsToggle
        hideAdFilters
        extraButtons={productSelect}
        locationProduct={
          openTab === `Voice Agent` ? `AI Agent` : openTab === `SMS Agent` ? `SMS Agent` : [`AI Agent`, `SMS Agent`]
        }
      />
      <DashboardTabs
        openTab={viewToTab(view)}
        onSetOpenTab={(tab) => handleViewChange(tabToView(tab))}
        tabOptions={[`Summary`, `Location Breakdown`]}
      />
    </>
  )

  if (!summary) {
    return (
      <PageScroll contentClassName="pb-14 pt-8" header={header}>
        {errorMessage ? (
          <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
            <p className="text-ui-md text-strong">Unable to load usage data</p>
            <p className="text-body-sm text-subtle">{errorMessage}</p>
          </div>
        ) : (
          <div className="flex items-center justify-center py-24 text-body-sm text-subtle">Loading usage data…</div>
        )}
      </PageScroll>
    )
  }

  const stats = buildStats(openTab, summary, { voiceTransferredCalls, avgSmsLatencyMs })

  return (
    <PageScroll contentClassName="pb-14 pt-8" header={header}>
      <div className="flex flex-col gap-y-6 md:gap-y-12">
        {errorMessage && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-body-sm text-amber-900">
            Showing the last successful data — a refresh failed: {errorMessage}
          </div>
        )}

        {view === `summary` ? (
          <>
            <SummaryTab
              statsCards={stats}
              hideChartOnMobile
              graphBorderless
              graph={
                <div className="flex flex-col gap-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-y-2">
                    <div className="flex items-center gap-2">
                      <p className="text-ui-lg text-strong">{chartTitleForTab(openTab)}</p>
                    </div>
                    <SegmentControl
                      label="Aggregated by:"
                      buttons={GRANULARITY_OPTIONS.map((option) => ({
                        label: granularityLabel(option),
                        onClick: () => handleGranularityChange(option),
                        isActive: option === granularity,
                      }))}
                    />
                  </div>
                  <UsageCostChart data={timeSeries} granularity={granularity} />
                </div>
              }
            />

            {summary && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {openTab === `All` ? (
                  <>
                    <CostCompositionPanel title="Voice Cost Composition" items={buildVoiceComposition(summary)} />
                    <CostCompositionPanel title="SMS Cost Composition" items={buildSmsComposition(summary)} />
                  </>
                ) : openTab === `Voice Agent` ? (
                  <CostCompositionPanel title="Voice Cost Composition" items={buildVoiceComposition(summary)} />
                ) : (
                  <CostCompositionPanel title="SMS Cost Composition" items={buildSmsComposition(summary)} />
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full flex-col">
            <SimpleTable
              activeSubpage="Location Breakdown"
              data={isLoading ? undefined : filteredLocationRows}
              columns={locationColumns}
              onSearchValueChange={(searchValue) => setLocationSearchQuery(searchValue)}
              showColumnOrdering
              enableExport
            />
          </div>
        )}
      </div>
    </PageScroll>
  )
}
